import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Tooltip, Button,
} from '@mui/material';
import {
  MonitorHeart, Launch, CheckCircle, PlayArrow, Pause, HourglassEmpty,
  Error as ErrorIcon, Refresh,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface BatchStep {
  step_number: number;
  step_name: string;
  status: string;
  is_critical: boolean;
  started_at: string | null;
  completed_at: string | null;
  performed_by: string | null;
  expected_duration_minutes: number | null;
}

interface MonitorBatch {
  id: string;
  batch_number: string;
  mbr_number: string;
  product_name: string;
  product_code: string;
  line_name: string | null;
  status: string;
  planned_start: string | null;
  actual_start: string | null;
  batch_size_value: string;
  batch_size_unit: string;
  batch_steps: BatchStep[];
}

const statusConfig: Record<string, { color: string; icon: React.ReactElement }> = {
  pending: { color: '#6B7280', icon: <HourglassEmpty fontSize="small" /> },
  in_progress: { color: '#3B82F6', icon: <PlayArrow fontSize="small" /> },
  completed: { color: '#10B981', icon: <CheckCircle fontSize="small" /> },
  approved: { color: '#22C55E', icon: <CheckCircle fontSize="small" /> },
  paused: { color: '#F59E0B', icon: <Pause fontSize="small" /> },
  rejected: { color: '#EF4444', icon: <ErrorIcon fontSize="small" /> },
};

const BatchMonitor: React.FC = () => {
  const [batches, setBatches] = useState<MonitorBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const loadBatches = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/batches/batch_monitor/');
      const data = await res.json();
      setBatches(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => {
    loadBatches();
    const interval = setInterval(loadBatches, 30000); // Auto-refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const getProgress = (batch: MonitorBatch) => {
    if (!batch.batch_steps.length) return 0;
    const completed = batch.batch_steps.filter(s => s.status === 'completed').length;
    return (completed / batch.batch_steps.length) * 100;
  };

  const getCurrentStep = (batch: MonitorBatch) => {
    return batch.batch_steps.find(s => s.status === 'in_progress') ||
           batch.batch_steps.find(s => s.status === 'pending');
  };

  const getElapsedTime = (start: string | null) => {
    if (!start) return '—';
    const ms = Date.now() - new Date(start).getTime();
    const hrs = Math.floor(ms / 3600000);
    const mins = Math.floor((ms % 3600000) / 60000);
    return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
  };

  const activeBatches = batches.filter(b => b.status === 'in_progress');
  const pendingBatches = batches.filter(b => b.status === 'pending');
  const completedBatches = batches.filter(b => ['completed', 'approved'].includes(b.status));

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MonitorHeart /> Batch Monitor
        </Typography>
        <Button startIcon={<Refresh />} onClick={loadBatches} variant="outlined" size="small">
          Refresh
        </Button>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={4}>
          <Card sx={{ borderLeft: '4px solid #3B82F6' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Active</Typography>
              <Typography variant="h3" color="#3B82F6">{activeBatches.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Card sx={{ borderLeft: '4px solid #F59E0B' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Pending</Typography>
              <Typography variant="h3" color="#F59E0B">{pendingBatches.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Card sx={{ borderLeft: '4px solid #10B981' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Completed</Typography>
              <Typography variant="h3" color="#10B981">{completedBatches.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Active Batch Cards */}
      {activeBatches.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, color: '#3B82F6' }}>▶ Active Batches</Typography>
          <Grid container spacing={2}>
            {activeBatches.map(batch => {
              const progress = getProgress(batch);
              const currentStep = getCurrentStep(batch);
              return (
                <Grid item xs={12} md={6} key={batch.id}>
                  <Card sx={{ border: '1px solid #3B82F6', position: 'relative', overflow: 'visible' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                          <Typography variant="h6" fontWeight="bold">{batch.batch_number}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {batch.product_name} ({batch.product_code})
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {batch.line_name || 'No line'} • {batch.batch_size_value} {batch.batch_size_unit}
                          </Typography>
                        </Box>
                        <Tooltip title="Open Batch Execution">
                          <IconButton onClick={() => navigate(`/batch-execution/${batch.id}`)} color="primary">
                            <Launch />
                          </IconButton>
                        </Tooltip>
                      </Box>

                      {/* Progress */}
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption">Progress</Typography>
                          <Typography variant="caption">{progress.toFixed(0)}%</Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={progress}
                          sx={{
                            height: 10, borderRadius: 5,
                            backgroundColor: '#374151',
                            '& .MuiLinearProgress-bar': { backgroundColor: '#3B82F6', borderRadius: 5 }
                          }}
                        />
                      </Box>

                      {/* Current Step */}
                      {currentStep && (
                        <Box sx={{ p: 1.5, backgroundColor: '#111827', borderRadius: 1, mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">Current Step</Typography>
                          <Typography variant="body1" fontWeight="bold">
                            Step {currentStep.step_number}: {currentStep.step_name}
                            {currentStep.is_critical && <Chip label="CRITICAL" size="small" sx={{ ml: 1, backgroundColor: '#EF444420', color: '#EF4444', fontSize: '0.6rem', height: 18 }} />}
                          </Typography>
                          {currentStep.performed_by && (
                            <Typography variant="caption" color="text.secondary">Operator: {currentStep.performed_by}</Typography>
                          )}
                        </Box>
                      )}

                      {/* Step indicators */}
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {batch.batch_steps.map(step => {
                          const sc = statusConfig[step.status] || statusConfig.pending;
                          return (
                            <Tooltip key={step.step_number} title={`${step.step_number}. ${step.step_name} (${step.status})`}>
                              <Box sx={{
                                width: 28, height: 28, borderRadius: '50%',
                                backgroundColor: `${sc.color}20`, border: `2px solid ${sc.color}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '0.7rem', fontWeight: 'bold', color: sc.color,
                              }}>
                                {step.step_number}
                              </Box>
                            </Tooltip>
                          );
                        })}
                      </Box>

                      <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.secondary">
                          Started: {batch.actual_start ? new Date(batch.actual_start).toLocaleString() : '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Elapsed: {getElapsedTime(batch.actual_start)}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* All Batches Table */}
      <Typography variant="h5" sx={{ mb: 2 }}>All Batches</Typography>
      <TableContainer component={Paper} sx={{ backgroundColor: '#1F2937' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 'bold', color: '#9CA3AF', borderBottom: '2px solid #374151' } }}>
              <TableCell>Batch</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Line</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Progress</TableCell>
              <TableCell>Current Step</TableCell>
              <TableCell>Elapsed</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {batches.map(batch => {
              const progress = getProgress(batch);
              const currentStep = getCurrentStep(batch);
              const sc = statusConfig[batch.status] || statusConfig.pending;
              return (
                <TableRow key={batch.id} hover>
                  <TableCell><Typography variant="body2" fontWeight="bold">{batch.batch_number}</Typography></TableCell>
                  <TableCell>
                    <Typography variant="body2">{batch.product_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{batch.product_code}</Typography>
                  </TableCell>
                  <TableCell>{batch.line_name || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      icon={sc.icon}
                      label={batch.status.replace('_', ' ')}
                      size="small"
                      sx={{ backgroundColor: `${sc.color}20`, color: sc.color, fontWeight: 'bold', textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: 120 }}>
                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      sx={{
                        height: 8, borderRadius: 4,
                        backgroundColor: '#374151',
                        '& .MuiLinearProgress-bar': { backgroundColor: progress >= 100 ? '#10B981' : '#3B82F6' }
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {batch.batch_steps.filter(s => s.status === 'completed').length}/{batch.batch_steps.length}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {currentStep ? (
                      <Typography variant="body2">
                        {currentStep.step_number}. {currentStep.step_name}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {progress >= 100 ? 'All complete' : '—'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{getElapsedTime(batch.actual_start)}</TableCell>
                  <TableCell>
                    <Tooltip title="Open">
                      <IconButton size="small" onClick={() => navigate(`/batch-execution/${batch.id}`)}>
                        <Launch fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default BatchMonitor;
